/**
 * Pricing math + display helpers for the nabidka / smlouva summary views
 * (DPH expansion, discount-as-percentage detection, equipment totals).
 */

import { parseFloatLoose } from '@/lib/format';
import type {
  NabidkaEquipmentItem,
  NabidkaSummaryCompany,
  NabidkaSummaryResponse,
  NabidkaVatRate,
  SmlouvaSummaryResponse,
} from './api';

export function vatMultiplier(rates: NabidkaVatRate[], idDph: number): number {
  const found = rates.find((r) => r.id_dph === idDph);
  if (!found) return 1.21;
  return Number(found.hodnota) || 1.21;
}

export function vatLabel(rates: NabidkaVatRate[], idDph: number): string {
  const found = rates.find((r) => r.id_dph === idDph);
  return found?.nazev_zkratka ?? '';
}

export function equipmentTotalBezDph(items: NabidkaEquipmentItem[]): number {
  return items.reduce((sum, row) => sum + parseFloatLoose(row.cena), 0);
}

export function equipmentTotalSDph(
  items: NabidkaEquipmentItem[],
  rates: NabidkaVatRate[],
): number {
  return items.reduce((sum, row) => {
    const cena = parseFloatLoose(row.cena);
    const mult = row.hodnota_dph ?? vatMultiplier(rates, row.id_dph);
    return sum + cena * mult;
  }, 0);
}

/**
 * Discount semantics: legacy stores both — `≤ 100` is percentage, `> 100` is
 * absolute (excl. VAT).
 */
export function discountAsAbsoluteBezDph(
  slevaZakladniCena: string | null | undefined,
  zakladniCenaBezDph: number,
): number {
  const sleva = Number(slevaZakladniCena) || 0;
  if (sleva <= 0) return 0;
  if (sleva > 100) return sleva;
  // percentage of base price (excl. VAT)
  return (zakladniCenaBezDph * sleva) / 100;
}

export function discountIsPercentage(slevaZakladniCena: string | null | undefined): boolean {
  const sleva = Number(slevaZakladniCena) || 0;
  return sleva > 0 && sleva <= 100;
}

/**
 * Discount in bez-DPH and s-DPH terms. A percentage discount must be applied
 * PER VAT BUCKET (base price + original equipment can mix rates): the API
 * repos that persist `cena_celkem` iterate the per-rate sums. Applying the base rate to the whole discount (the old
 * behavior here) printed totals that disagreed with the stored cena_celkem
 * whenever an original item carried a different rate.
 * An absolute discount (> 100) stays a single line at the offer's base rate.
 */
export function computeDiscount(
  rates: NabidkaVatRate[],
  offer: { id_dph: number; zakladni_cena: string | number | null; sleva_zakladni_cena: string | null | undefined },
  original: NabidkaEquipmentItem[],
): { bez: number; sDph: number } {
  const sleva = Number(offer.sleva_zakladni_cena) || 0;
  if (sleva <= 0) return { bez: 0, sDph: 0 };
  const baseMult = vatMultiplier(rates, offer.id_dph);
  if (sleva > 100) return { bez: sleva, sDph: sleva * baseMult };

  const buckets = new Map<number, number>(); // multiplier -> bez-DPH sum
  const add = (mult: number, amount: number) =>
    buckets.set(mult, (buckets.get(mult) ?? 0) + amount);
  add(baseMult, Number(offer.zakladni_cena) || 0);
  for (const row of original) {
    add(row.hodnota_dph ?? vatMultiplier(rates, row.id_dph), parseFloatLoose(row.cena));
  }

  let bez = 0;
  let sDph = 0;
  for (const [mult, value] of buckets) {
    if (value === 0) continue;
    const part = (value * sleva) / 100;
    bez += part;
    sDph += part * mult;
  }
  return { bez, sDph };
}

export function formatCompanyAddress(c: NabidkaSummaryCompany | null | undefined): {
  street: string;
  cityLine: string;
} {
  if (!c) return { street: '', cityLine: '' };
  const street = [c.ulice, c.cp].filter(Boolean).join(' ').trim();
  const cityLine = [c.psc, c.mesto].filter(Boolean).join(' ').trim();
  return { street, cityLine };
}

export interface SummaryTotals {
  zakladniBezDph: number;
  zakladniSDph: number;
  origBez: number;
  origSDph: number;
  neoriginalBez: number;
  neoriginalSDph: number;
  slevaBez: number;
  slevaSDph: number;
  celkemBez: number;
  celkemSDph: number;
  isPercentage: boolean;
}

export function computeSummaryTotals(
  data: NabidkaSummaryResponse | SmlouvaSummaryResponse,
): SummaryTotals {
  const rates = data.vatRates;
  const offer = 'nabidka' in data ? data.nabidka : data.smlouva;
  const baseDphMult = vatMultiplier(rates, offer.id_dph);
  const zakladniBezDph = Number(offer.zakladni_cena) || 0;
  const zakladniSDph = zakladniBezDph * baseDphMult;
  const origBez = equipmentTotalBezDph(data.equipment.original);
  const origSDph = equipmentTotalSDph(data.equipment.original, rates);
  const neoriginalBez = equipmentTotalBezDph(data.equipment.neoriginal);
  const neoriginalSDph = equipmentTotalSDph(data.equipment.neoriginal, rates);
  // Percentage discount applies only to base price + original equipment —
  // never to dodatečná výbava (neoriginal). Mirrors the backend repos
  // ("Nevztahuje se na dodatečné příslušenství").
  const { bez: slevaBez, sDph: slevaSDph } = computeDiscount(
    rates,
    offer,
    data.equipment.original,
  );
  const celkemBez = zakladniBezDph + origBez + neoriginalBez - slevaBez;
  const celkemSDph = zakladniSDph + origSDph + neoriginalSDph - slevaSDph;
  return {
    zakladniBezDph,
    zakladniSDph,
    origBez,
    origSDph,
    neoriginalBez,
    neoriginalSDph,
    slevaBez,
    slevaSDph,
    celkemBez,
    celkemSDph,
    isPercentage: discountIsPercentage(offer.sleva_zakladni_cena),
  };
}

/**
 * Build the document title used as the default "Save as PDF" filename when the
 * print view is sent to the browser print dialog. Browsers derive the suggested
 * filename from `document.title`, so we join the document label, its number and
 * the counterparty name, then strip filesystem-reserved characters.
 *
 *   buildPrintFileTitle('Cenová nabídka', 202600619, 'Karel Dvořák')
 *   // → 'Cenová nabídka 202600619 – Karel Dvořák'
 */
export function buildPrintFileTitle(
  label: string,
  docNumber: number | string | null | undefined,
  party: string | null | undefined,
): string {
  const base = [label, docNumber != null ? String(docNumber) : null]
    .filter(Boolean)
    .join(' ');
  const name = party?.trim();
  return [base, name]
    .filter(Boolean)
    .join(' – ')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function formatVehicleHeading(
  brand: string | null | undefined,
  model: string | null | undefined,
): string {
  return [brand, model].filter(Boolean).join(' ').trim() || 'Vozidlo';
}

export interface VatBreakdownEntry {
  /** Display label for the rate, e.g. "21 %" or "15 %". */
  label: string;
  /** VAT rate value (1.21, 1.15, …) for downstream math. */
  multiplier: number;
  /** Sum of `cena` (excl. VAT) of items in this bucket. */
  bezDph: number;
  /** `bezDph * multiplier`. */
  sDph: number;
  /** Just the tax portion. */
  dph: number;
}

/**
 * Aggregate equipment + base price by actual per-item VAT rate. Used by the
 * Souhrn to show the real rate mix when a nabídka has neoriginal items at a
 * reduced rate alongside originals at the standard rate.
 */
export function computeVatBreakdown(
  data: NabidkaSummaryResponse | SmlouvaSummaryResponse,
): VatBreakdownEntry[] {
  const rates = data.vatRates;
  const offer = 'nabidka' in data ? data.nabidka : data.smlouva;
  const buckets = new Map<number, VatBreakdownEntry>();

  const add = (idDph: number, hodnotaDph: number | null | undefined, cena: number) => {
    if (cena === 0) return;
    const mult = hodnotaDph ?? vatMultiplier(rates, idDph);
    const label = vatLabel(rates, idDph) || `${Math.round((mult - 1) * 100)} %`;
    const key = Math.round(mult * 10000);
    const sDph = cena * mult;
    const dph = sDph - cena;
    const existing = buckets.get(key);
    if (existing) {
      existing.bezDph += cena;
      existing.sDph += sDph;
      existing.dph += dph;
    } else {
      buckets.set(key, { label, multiplier: mult, bezDph: cena, sDph, dph });
    }
  };

  const zakladniBez = Number(offer.zakladni_cena) || 0;
  add(offer.id_dph, null, zakladniBez);
  for (const row of data.equipment.original) {
    add(row.id_dph, row.hodnota_dph ?? null, parseFloatLoose(row.cena));
  }
  for (const row of data.equipment.neoriginal) {
    add(row.id_dph, row.hodnota_dph ?? null, parseFloatLoose(row.cena));
  }

  // The percentage discount basis is base price + original equipment only,
  // never dodatečná výbava (neoriginal) — and it is subtracted from each VAT
  // bucket proportionally (the backend repos iterate the per-rate sums).
  // An absolute discount (> 100) stays a single base-rate line.
  const slevaRaw = Number(offer.sleva_zakladni_cena) || 0;
  if (slevaRaw > 100) {
    add(offer.id_dph, null, -slevaRaw);
  } else if (slevaRaw > 0) {
    add(offer.id_dph, null, -(zakladniBez * slevaRaw) / 100);
    for (const row of data.equipment.original) {
      add(row.id_dph, row.hodnota_dph ?? null, -(parseFloatLoose(row.cena) * slevaRaw) / 100);
    }
  }

  return Array.from(buckets.values())
    .filter((b) => b.bezDph !== 0)
    .sort((a, b) => b.multiplier - a.multiplier);
}

/** "1 den" / "2 dny" / "7 dnů" — Czech plural for the offer validity. */
export function formatDays(n: number): string {
  if (n === 1) return '1 den';
  if (n >= 2 && n <= 4) return `${n} dny`;
  return `${n} dnů`;
}
